import { useEffect, useState } from 'react';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useErrorMessage } from '../../../utils/errorMessage';

/**
 * Custom hook that manages address-based filtering (city and department/state) for list components.
 * This hook handles fetching unique cities and departments from the API and managing filter state.
 *
 * @param {string} resourceName - The name of the resource to fetch (e.g., 'owners', 'pensioners')
 * @returns {Object} An object containing:
 *   - cityOptions: Array of unique city names
 *   - departmentOptions: Array of unique department/state names
 *   - cityFilter: Current city filter value
 *   - departmentFilter: Current department filter value
 *   - setCityFilter: Function to set the city filter
 *   - setDepartmentFilter: Function to set the department filter
 *   - isFieldsLoading: Boolean indicating if fields are being loaded
 *   - extraQuery: Query string built from active filters
 */
export const useListWithAddressFilters = (resourceName) => {
  const [cityOptions, setCityOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [cityFilter, setCityFilter] = useState();
  const [departmentFilter, setDepartmentFilter] = useState();
  const [isFieldsLoading, setIsFieldsLoading] = useState(true);
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();

  const queryParts = [];
  if (cityFilter) {
    queryParts.push(`address.city=${cityFilter}`);
  }
  if (departmentFilter) {
    queryParts.push(`address.state=${departmentFilter}`);
  }
  const extraQuery = queryParts.join('&');

  const fetchOptions = async () => {
    try {
      setIsFieldsLoading(true);
      const { data } = await dispatchAPI('GET', {
        url: `${resourceName}?fields=address`
      });
      const uniqueStates = Array.from(
        new Set(data.map((item) => item?.address?.state).filter(Boolean))
      );
      const uniqueCities = Array.from(
        new Set(data.map((item) => item?.address?.city).filter(Boolean))
      );

      setDepartmentOptions(uniqueStates);
      setCityOptions(uniqueCities);
      setIsFieldsLoading(false);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      await fetchOptions();
    })();
  }, [resourceName]);

  return {
    cityOptions,
    departmentOptions,
    cityFilter,
    departmentFilter,
    setCityFilter,
    setDepartmentFilter,
    isFieldsLoading,
    extraQuery
  };
};
